import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { paymentCalculator } from '../../services/PaymentCalculator';
import PaymentApprovalModal from './PaymentApprovalModal';
import {
    DollarSign,
    Clock,
    CheckCircle,
    AlertCircle,
    Plus,
    RefreshCw,
    Eye
} from 'lucide-react';

export default function AdminPaymentsPanel() {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('unpaid'); // unpaid, pending, approved, paid
    
    // Data
    const [completedJobs, setCompletedJobs] = useState([]);
    const [payments, setPayments] = useState([]);
    const [contractors, setContractors] = useState({});
    
    // Modal
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    
    // Stats
    const [stats, setStats] = useState({
        unpaidJobs: 0,
        pendingPayments: 0,
        approvedPayments: 0,
        paidPayments: 0,
        totalPending: 0
    });

    useEffect(() => {
        loadData();
    }, []);

    // Recalculate stats whenever jobs or payments change
    useEffect(() => {
        if (completedJobs.length > 0 || payments.length > 0) {
            calculateStats(completedJobs, payments);
        }
    }, [completedJobs, payments]);

    const loadData = async () => {
        setLoading(true);

        try {
            // Load contractors (for mapping)
            const contractorsSnapshot = await getDocs(collection(db, 'contractors'));
            const contractorsMap = {};
            contractorsSnapshot.docs.forEach(doc => {
                contractorsMap[doc.id] = doc.data();
            });
            setContractors(contractorsMap);

            // Listen to completed jobs
            const jobsQuery = query(
                collection(db, 'workOrders'),
                where('status', '==', 'completed')
            );

            const unsubJobs = onSnapshot(jobsQuery, (snapshot) => {
                const jobs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCompletedJobs(jobs);
            });

            // Listen to payments
            const unsubPayments = onSnapshot(collection(db, 'contractorPayments'), (snapshot) => {
                const paymentsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                // Sort by date (newest first)
                paymentsData.sort((a, b) => 
                    new Date(b.createdAt) - new Date(a.createdAt)
                );
                
                setPayments(paymentsData);
            });

            setLoading(false);

        } catch (error) {
            console.error('Error loading payments data:', error);
            setLoading(false);
        }
    };

    const calculateStats = (jobs, paymentsData) => {
        const unpaidJobs = jobs.filter(j => !j.contractorPaymentId && j.contractorId).length;
        const pending = paymentsData.filter(p => p.status === 'pending').length;
        const approved = paymentsData.filter(p => p.status === 'approved').length;
        const paid = paymentsData.filter(p => p.status === 'paid').length;
        
        const totalPending = paymentsData
            .filter(p => p.status === 'pending' || p.status === 'approved')
            .reduce((sum, p) => sum + (p.calculatedAmount || 0), 0);

        setStats({
            unpaidJobs,
            pendingPayments: pending,
            approvedPayments: approved,
            paidPayments: paid,
            totalPending
        });
    };

    const handleCreatePayment = async (job) => {
        // Prompt for hours
        const hours = prompt(`How many hours did the contractor work on this job?\n\nJob: ${job.serviceType}\nContractor: ${contractors[job.contractorId]?.name || 'Unknown'}`);
        
        if (!hours || isNaN(hours)) {
            alert('Invalid hours entered');
            return;
        }

        try {
            // Calculate payment
            const paymentData = await paymentCalculator.calculateJobPayment(
                job.id,
                parseFloat(hours)
            );

            // Create payment record
            await paymentCalculator.createPayment(paymentData);

            alert(`✅ Payment created for ${contractors[job.contractorId]?.name}!\nAmount: $${paymentData.calculatedAmount.toFixed(2)}`);

        } catch (error) {
            console.error('Error creating payment:', error);
            alert('Failed to create payment. Check console for details.');
        }
    };

    const handleViewPayment = (payment) => {
        setSelectedPayment(payment);
        setShowPaymentModal(true);
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-blue-100 text-blue-800',
            'paid': 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        if (status === 'pending') return <Clock className="w-4 h-4" />;
        if (status === 'approved') return <CheckCircle className="w-4 h-4" />;
        if (status === 'paid') return <DollarSign className="w-4 h-4" />;
        return null;
    };

    // Filter data based on active tab
    const getFilteredData = () => {
        switch (activeTab) {
            case 'unpaid':
                return completedJobs.filter(j => !j.contractorPaymentId && j.contractorId);
            case 'pending':
                return payments.filter(p => p.status === 'pending');
            case 'approved':
                return payments.filter(p => p.status === 'approved');
            case 'paid':
                return payments.filter(p => p.status === 'paid');
            default:
                return [];
        }
    };

    const filteredData = getFilteredData();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading payments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Contractor Payments</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Manage contractor payments and approvals
                    </p>
                </div>
                <button
                    onClick={loadData}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Unpaid Jobs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Unpaid Jobs</p>
                        <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.unpaidJobs}</p>
                </div>

                {/* Pending */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Pending</p>
                        <Clock className="w-5 h-5 text-yellow-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingPayments}</p>
                </div>

                {/* Approved */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Approved</p>
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.approvedPayments}</p>
                </div>

                {/* Paid */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Paid</p>
                        <DollarSign className="w-5 h-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.paidPayments}</p>
                </div>

                {/* Total Pending Amount */}
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-sm p-4 text-white">
                    <p className="text-sm mb-2 opacity-90">Total Pending</p>
                    <p className="text-2xl font-bold">${stats.totalPending.toFixed(2)}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('unpaid')}
                        className={`flex-1 px-6 py-3 font-medium transition-colors ${
                            activeTab === 'unpaid'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Unpaid Jobs ({stats.unpaidJobs})
                    </button>
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`flex-1 px-6 py-3 font-medium transition-colors ${
                            activeTab === 'pending'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Pending ({stats.pendingPayments})
                    </button>
                    <button
                        onClick={() => setActiveTab('approved')}
                        className={`flex-1 px-6 py-3 font-medium transition-colors ${
                            activeTab === 'approved'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Approved ({stats.approvedPayments})
                    </button>
                    <button
                        onClick={() => setActiveTab('paid')}
                        className={`flex-1 px-6 py-3 font-medium transition-colors ${
                            activeTab === 'paid'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Paid ({stats.paidPayments})
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {activeTab === 'unpaid' ? (
                        // Unpaid Jobs List
                        <div className="space-y-4">
                            {filteredData.length > 0 ? (
                                filteredData.map((job) => {
                                    const contractor = contractors[job.contractorId];
                                    
                                    return (
                                        <div
                                            key={job.id}
                                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-gray-900">
                                                        {job.serviceType}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {job.location}
                                                    </p>
                                                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                                                        <span>Contractor: {contractor?.name || 'Unknown'}</span>
                                                        <span>•</span>
                                                        <span>Rate: ${contractor?.hourlyRate || 18}/hr</span>
                                                        <span>•</span>
                                                        <span>Completed: {new Date(job.completedAt || job.updatedAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleCreatePayment(job)}
                                                    className="ml-4 flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    <span>Create Payment</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-12">
                                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600">All completed jobs have payments!</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Payments List (Pending/Approved/Paid)
                        <div className="space-y-4">
                            {filteredData.length > 0 ? (
                                filteredData.map((payment) => (
                                    <div
                                        key={payment.id}
                                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h3 className="font-semibold text-gray-900">
                                                        {payment.contractorName}
                                                    </h3>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                                                        {getStatusIcon(payment.status)}
                                                        <span className="ml-1">{payment.status.toUpperCase()}</span>
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    {payment.jobType} • {payment.jobLocation}
                                                </p>
                                                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                                                    {payment.hoursWorked && (
                                                        <span>{payment.hoursWorked} hrs × ${payment.hourlyRate}/hr</span>
                                                    )}
                                                    <span>•</span>
                                                    <span>Created: {new Date(payment.createdAt).toLocaleDateString()}</span>
                                                    {payment.approvedAt && (
                                                        <>
                                                            <span>•</span>
                                                            <span>Approved: {new Date(payment.approvedAt).toLocaleDateString()}</span>
                                                        </>
                                                    )}
                                                    {payment.paidAt && (
                                                        <>
                                                            <span>•</span>
                                                            <span>Paid: {new Date(payment.paidAt).toLocaleDateString()}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ml-4 text-right flex items-center space-x-4">
                                                <div>
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        ${payment.calculatedAmount?.toFixed(2) || '0.00'}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => handleViewPayment(payment)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12">
                                    <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600">No {activeTab} payments</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Payment Modal */}
            {showPaymentModal && selectedPayment && (
                <PaymentApprovalModal
                    payment={selectedPayment}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setSelectedPayment(null);
                    }}
                    onApprove={() => {
                        setShowPaymentModal(false);
                        setSelectedPayment(null);
                        loadData();
                    }}
                />
            )}
        </div>
    );
}
